import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  ACTIVE_STATUSES,
  COMPETITION_SORTS,
  DEFAULT_COMPETITION_SORT,
  DEFAULT_SORT_ORDER,
  SORT_ORDERS,
  type CompetitionSort,
  type CompetitionsQuery,
  type SortOrder,
} from '@/api/competitions'
import type { GlobalFilters } from '@/lib/filters'

/*
 * The page's own URL vocabulary: status, category, sort, order and a KPI's metric, read,
 * validated and written beside the global filters, and the list query they make.
 */

/** The "Active" status chip, and the Overview KPI's drill-down (`?status=active`): every on-sale status. */
export const ACTIVE = 'active'

const STATUS_PARAM = 'status'
const CATEGORY_PARAM = 'category'
const SORT_PARAM = 'sort'
const ORDER_PARAM = 'order'
/** A KPI's drill-down: `?metric=tickets` opens the list sorted by tickets sold. */
const METRIC_PARAM = 'metric'

/** What the API takes for one filter value: 1 to 100 characters. */
const MAX_FILTER_VALUE = 100

/**
 * The KPI drill-downs' metrics and the sort each opens: `metric` is the Overview's
 * vocabulary, not a second way to write `sort`, so it takes these keys only.
 */
const METRIC_SORTS: Readonly<Record<string, CompetitionSort>> = { tickets: 'tickets_sold_in_range' }

function isSort(value: string | null): value is CompetitionSort {
  return value !== null && (COMPETITION_SORTS as readonly string[]).includes(value)
}

function isOrder(value: string | null): value is SortOrder {
  return value !== null && (SORT_ORDERS as readonly string[]).includes(value)
}

/** A filter value the API would accept, or none: blank and over-long values are dropped, not sent to a 422. */
function filterValue(raw: string | null): string | undefined {
  const value = raw?.trim()
  return value && value.length <= MAX_FILTER_VALUE ? value : undefined
}

function metricSort(metric: string): CompetitionSort | undefined {
  return Object.hasOwn(METRIC_SORTS, metric) ? METRIC_SORTS[metric] : undefined
}

/** The page's parameters a link carries that the page cannot use: left in place, they would only mislead. */
function unusable(params: URLSearchParams): string[] {
  const stale: string[] = []
  const check = (param: string, usable: (value: string) => boolean) => {
    const value = params.get(param)
    if (value !== null && !usable(value)) stale.push(param)
  }
  check(SORT_PARAM, isSort)
  check(ORDER_PARAM, isOrder)
  check(METRIC_PARAM, (value) => metricSort(value) !== undefined)
  check(STATUS_PARAM, (value) => filterValue(value) !== undefined)
  check(CATEGORY_PARAM, (value) => filterValue(value) !== undefined)
  return stale
}

/** The sort a link asks for: `sort` when valid, else a KPI's `metric`, else the default. */
function sortFrom(params: URLSearchParams): CompetitionSort {
  const sort = params.get(SORT_PARAM)
  if (isSort(sort)) return sort
  const metric = params.get(METRIC_PARAM)
  return (metric === null ? undefined : metricSort(metric)) ?? DEFAULT_COMPETITION_SORT
}

export interface CompetitionParams {
  /** A status word, `ACTIVE`, or none (every status). */
  status: string | undefined
  category: string | undefined
  sort: CompetitionSort
  order: SortOrder
  setStatus: (status: string | undefined) => void
  setCategory: (category: string | undefined) => void
  /** `null`, or a key the API does not take, restores the default order. */
  setSort: (sort: { sort: string; order: SortOrder } | null) => void
  clearFilters: () => void
}

/** The page's own filters and sort, kept in the URL beside the global filters so a link reproduces the view. Defaults are left out. */
export function useCompetitionParams(): CompetitionParams {
  const [params, setParams] = useSearchParams()

  // A value the page cannot use (a mistyped sort, a blank status) is dropped from the URL, so the link shows what the page shows.
  useEffect(() => {
    const stale = unusable(params)
    if (stale.length === 0) return
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        for (const param of stale) next.delete(param)
        return next
      },
      { replace: true },
    )
  }, [params, setParams])

  const update = (change: (next: URLSearchParams) => void) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        change(next)
        return next
      },
      { replace: true },
    )
  const setOrDelete = (next: URLSearchParams, param: string, value: string | undefined) => {
    if (value === undefined) next.delete(param)
    else next.set(param, value)
  }

  const order = params.get(ORDER_PARAM)
  return {
    status: filterValue(params.get(STATUS_PARAM)),
    category: filterValue(params.get(CATEGORY_PARAM)),
    sort: sortFrom(params),
    order: isOrder(order) ? order : DEFAULT_SORT_ORDER,
    setStatus: (status) => update((next) => setOrDelete(next, STATUS_PARAM, status)),
    setCategory: (category) => update((next) => setOrDelete(next, CATEGORY_PARAM, category)),
    setSort: (choice) =>
      update((next) => {
        // A choice made here replaces the drill-down's metric: the URL says the sort itself.
        next.delete(METRIC_PARAM)
        const isDefault =
          !choice || !isSort(choice.sort) || (choice.sort === DEFAULT_COMPETITION_SORT && choice.order === DEFAULT_SORT_ORDER)
        setOrDelete(next, SORT_PARAM, isDefault ? undefined : choice.sort)
        setOrDelete(next, ORDER_PARAM, isDefault ? undefined : choice.order)
      }),
    clearFilters: () =>
      update((next) => {
        next.delete(STATUS_PARAM)
        next.delete(CATEGORY_PARAM)
      }),
  }
}

/**
 * What a competition's page carries back to this list: the list's own parameters at the
 * moment a row was opened, as one value of `LIST_RETURN_PARAM`, so "All competitions"
 * returns to the same status, category and sort. Empty when the list was unfiltered.
 */
export const LIST_RETURN_PARAM = 'list'
const LIST_STATE_PARAMS: readonly string[] = [STATUS_PARAM, CATEGORY_PARAM, SORT_PARAM, ORDER_PARAM, METRIC_PARAM]

export function listReturnState(search: URLSearchParams): string {
  const state = new URLSearchParams()
  for (const [key, value] of search) if (LIST_STATE_PARAMS.includes(key)) state.append(key, value)
  return state.toString()
}

/**
 * The list's own parameters a returning link may restore: only the list's keys, so a
 * hand-edited `list` value can add nothing else to the URL. The list page validates
 * their values itself (`useCompetitionParams` drops what it cannot use).
 */
export function listStateParams(state: string | null): [string, string][] {
  return [...new URLSearchParams(state ?? '')].filter(([key]) => LIST_STATE_PARAMS.includes(key))
}

/** The status words a status choice sends: `ACTIVE` is every on-sale status, as the Overview counts them. */
export function statusesFor(status: string | undefined): string[] {
  if (status === undefined) return []
  return status.toLowerCase() === ACTIVE ? [...ACTIVE_STATUSES] : [status]
}

/** The list's query, less its page: the global scope, the page's filters and the sort. */
export function useCompetitionsQuery(filters: GlobalFilters, params: CompetitionParams): CompetitionsQuery {
  const { status, category, sort, order } = params
  return useMemo(
    () => ({ filters, statuses: statusesFor(status), categories: category ? [category] : [], sort, order }),
    [filters, status, category, sort, order],
  )
}
